import axios from 'axios'
import request from '@api/helpers/authorizateRequest'
import { rethrowBlobError } from '@api/modules/operatorMonitoring'

const BASE_URL = '/marketing'
// Lives under /drivers, not /marketing, and answers with the legacy { response } envelope.
const MANUAL_ORIGINS_URL = '/drivers/marketing-origins'
const CANDIDATE_ORIGINS_URL = '/candidates/marketing-origins'
const CANDIDATE_SOURCE_URL = id => '/candidates/' + Number(id) + '/marketing-source'
// Driver creation waits for this answer, so a hung backend must not block the form for long.
const MANUAL_ORIGINS_TIMEOUT_MS = 10000
const ENDPOINTS = {
    capabilities: '/capabilities',
    featureCompanies: '/feature/companies',
    feature: '/feature',
    reference: '/reference',
    channels: '/channels',
    channel: id => '/channels/' + id,
    channelFlows: id => '/channels/' + id + '/flows',
    channelValidation: id => '/channels/' + id + '/validate',
    routeKey: id => '/channels/' + id + '/route-key',
    channelActivation: id => '/channels/' + id + '/activate',
    channelPause: id => '/channels/' + id + '/pause',
    pendingTasks: id => '/channels/' + id + '/pending',
    resumeTasks: id => '/channels/' + id + '/resume',
    rules: '/rules',
    connections: '/connections',
    connectionRotation: id => '/connections/' + id + '/rotate-secret',
    connectionVerification: id => '/connections/' + id + '/verify',
    connectionBots: id => '/connections/' + id + '/bots',
    overview: '/overview',
    events: '/events',
    event: id => '/events/' + id,
    retryEvent: id => '/events/' + id + '/retry',
    resolveEvent: id => '/events/' + id + '/resolve',
    audit: '/audit',
    verifications: '/verifications',
    exports: '/exports',
    exportStatus: id => '/exports/' + id,
    download: id => '/exports/' + Number(id) + '/download',
    scanHistory: '/identity-history/scan',
    unresolvedProfiles: '/identity-history/unresolved',
    verifyHistory: '/identity-history/verify',
    collectionStart: '/collection/start',
    enrollRegistration: '/registration-adapter/enroll',
    scopes: '/user-scopes',
    linkIdentity: '/subjects/link',
    mergeSubjects: '/subjects/merge',
}
const data = response => response.data
const get = (endpoint, params, cancelToken) => request.get(BASE_URL + endpoint, { params, cancelToken }).then(data)
const post = (endpoint, body, cancelToken) => request.post(BASE_URL + endpoint, body, { cancelToken }).then(data)

export default {
    cancelSource: () => axios.CancelToken.source(),
    isCancel: error => axios.isCancel(error),
    manualOrigins: (chatId, token) => request.get(MANUAL_ORIGINS_URL, {
        params: chatId === null ? {} : { chat_id: chatId },
        cancelToken: token,
        timeout: MANUAL_ORIGINS_TIMEOUT_MS,
    }).then(response => response.data.response),
    candidateOrigins: (id, token) => request.get(CANDIDATE_ORIGINS_URL, {
        params: { id: Number(id) },
        cancelToken: token,
        timeout: MANUAL_ORIGINS_TIMEOUT_MS,
    }).then(response => response.data.response),
    saveCandidateSource: (id, payload, token) => request.post(CANDIDATE_SOURCE_URL(id), payload, {
        cancelToken: token,
        timeout: MANUAL_ORIGINS_TIMEOUT_MS,
    }).then(response => response.data.response),
    capabilities: token => get(ENDPOINTS.capabilities, {}, token),
    featureCompanies: token => get(ENDPOINTS.featureCompanies, {}, token),
    setFeature: (payload, token) => post(ENDPOINTS.feature, payload, token),
    reference: token => get(ENDPOINTS.reference, {}, token),
    channels: token => get(ENDPOINTS.channels, {}, token),
    channelFlows: (id, token) => get(ENDPOINTS.channelFlows(id), {}, token),
    rules: token => get(ENDPOINTS.rules, {}, token),
    connections: token => get(ENDPOINTS.connections, {}, token),
    connectionBots: (id, token) => get(ENDPOINTS.connectionBots(id), {}, token),
    saveConnection: (form, token) => post(form.id ? ENDPOINTS.connectionRotation(form.id) : ENDPOINTS.connections, form, token),
    verifyConnection: (id, token) => post(ENDPOINTS.connectionVerification(id), {}, token),
    saveChannel(form, cancelToken) {
        if (!form.id) return post(ENDPOINTS.channels, form, cancelToken)
        return request.patch(BASE_URL + ENDPOINTS.channel(form.id), form, { cancelToken }).then(data)
    },
    saveRule: (payload, token) => post(ENDPOINTS.rules, payload, token),
    validateChannel: (id, token) => post(ENDPOINTS.channelValidation(id), {}, token),
    generateRouteKey: (id, payload, token) => post(ENDPOINTS.routeKey(id), payload, token),
    activateChannel: (id, payload, token) => post(ENDPOINTS.channelActivation(id), payload, token),
    pauseChannel: (id, payload, token) => post(ENDPOINTS.channelPause(id), payload, token),
    pendingTasks: (id, token) => get(ENDPOINTS.pendingTasks(id), {}, token),
    resumePendingTasks: (id, payload, token) => post(ENDPOINTS.resumeTasks(id), payload, token),
    overview: (params, token) => get(ENDPOINTS.overview, params, token),
    events: (params, token) => get(ENDPOINTS.events, params, token),
    event: (id, token) => get(ENDPOINTS.event(id), {}, token),
    retryEvent: (id, payload, token) => post(ENDPOINTS.retryEvent(id), payload, token),
    resolveEvent: (id, payload, token) => post(ENDPOINTS.resolveEvent(id), payload, token),
    audit: (params, token) => get(ENDPOINTS.audit, params, token),
    saveVerification: (payload, token) => post(ENDPOINTS.verifications, payload, token),
    createExport: (params, token) => post(ENDPOINTS.exports, params, token),
    exportStatus: (id, token) => get(ENDPOINTS.exportStatus(id), {}, token),
    download: (id, cancelToken) => request.get(BASE_URL + ENDPOINTS.download(id), { responseType: 'blob', cancelToken }).then(data).catch(rethrowBlobError),
    scanHistory: token => post(ENDPOINTS.scanHistory, {}, token),
    unresolvedProfiles: (params, token) => get(ENDPOINTS.unresolvedProfiles, params, token),
    verifyHistory: (payload, token) => post(ENDPOINTS.verifyHistory, payload, token),
    startCollection: token => post(ENDPOINTS.collectionStart, {}, token),
    enrollRegistration: (payload, token) => post(ENDPOINTS.enrollRegistration, payload, token),
    saveScope: (payload, token) => post(ENDPOINTS.scopes, payload, token),
    linkIdentity: (payload, token) => post(ENDPOINTS.linkIdentity, payload, token),
    mergeSubjects: (payload, token) => post(ENDPOINTS.mergeSubjects, payload, token),
}
